import { zodResolver } from "@hookform/resolvers/zod";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Slider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toast } from "sonner";

import { useDeleteAllActorVoiceTrainingFilesMutation } from "Actors/Api/Mutations/useDeleteAllActorVoiceTrainingFilesMutation";
import { useGenerateActorVoicePreviewMutation } from "Actors/Api/Mutations/useGenerateActorVoicePreviewMutation";
import { useUpdateActorVoiceMutation } from "Actors/Api/Mutations/useUpdateActorVoiceMutation";
import { useUploadActorVoiceTrainingFileMutation } from "Actors/Api/Mutations/useUploadActorVoiceTrainingFileMutation";
import { useActorVoiceDetailQuery } from "Actors/Api/Queries/useActorVoiceDetailQuery";
import { ActorVoiceAudioPlayer } from "Actors/Components/ActorVoiceAudioPlayer";
import {
  type EditVoiceFormData,
  editVoiceFormSchema,
} from "Actors/Forms/voiceFormSchema";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import { getLanguageOptions } from "Forms/Utils/EnumOptions";
import {
  type ActorVoiceDetailDto,
  ActorVoiceStatus,
  LanguageCodes,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LANGUAGE_LABELS } from "Infrastructure/Utils/EnumUtils";
import { BlModal } from "Shared/Dialogs/BlModal";

interface EditActorVoiceModalProps {
  isOpen: boolean;
  actorID: string;
  actorVoiceID: string;
  onClose: () => void;
}

export const EditActorVoiceModal = ({
  isOpen,
  actorID,
  actorVoiceID,
  onClose,
}: EditActorVoiceModalProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [speedFactor, setSpeedFactor] = useState(1.0);
  const [volumePercent, setVolumePercent] = useState(100);

  const { data: voiceDetail, isLoading: isLoadingDetail } =
    useActorVoiceDetailQuery(actorID, actorVoiceID);

  const updateMutation = useUpdateActorVoiceMutation(actorID);
  const uploadMutation = useUploadActorVoiceTrainingFileMutation(actorID);
  const deleteAllMutation =
    useDeleteAllActorVoiceTrainingFilesMutation(actorID);
  const generatePreviewMutation = useGenerateActorVoicePreviewMutation(actorID);

  const internalFormRef = useRef<UseFormReturn<EditVoiceFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => buildDefaults(voiceDetail),
    [voiceDetail],
    internalFormRef,
  );

  const form = useForm<EditVoiceFormData>({
    resolver: zodResolver(editVoiceFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const trainingFilesCount = voiceDetail?.trainingFilesCount ?? 0;
  const needsFileUpload = trainingFilesCount === 0;

  const canShowPreview =
    voiceDetail &&
    [
      ActorVoiceStatus.Draft,
      ActorVoiceStatus.UploadingTrainingFiles,
      ActorVoiceStatus.Active,
    ].includes(voiceDetail.status);

  const onSubmit = async (data: EditVoiceFormData) => {
    try {
      await updateMutation.mutateAsync({
        actorVoiceID,
        request: {
          name: data.name,
          tags: data.tags || undefined,
          voiceLlm: voiceDetail!.voiceLlm,
          language: data.language,
          externalVoiceID: voiceDetail!.externalVoiceID || undefined,
        },
      });
      toast.success(
        t(Resources.Actors.ActorDetail.EditVoiceModal.SuccessMessage),
      );
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via processResponse
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    for (const file of files) {
      uploadMutation.mutate({ actorVoiceID, file });
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveAllFiles = () => {
    deleteAllMutation.mutate(actorVoiceID, {
      onSuccess: () => toast.success(t(Resources.Common.SaveSuccess)),
    });
  };

  const handleGeneratePreview = () => {
    generatePreviewMutation.mutate(
      { actorVoiceID, request: { speedFactor, volumePercent } },
      { onSuccess: () => toast.success(t(Resources.Common.SaveSuccess)) },
    );
  };

  const renderFileUploadArea = () => (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, mb: Spacing.XS }}
      >
        {t(Resources.Actors.ActorDetail.UploadTrainingData)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: Spacing.S,
          p: Spacing.S,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="audio/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <Button
          variant="text"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          sx={{ textTransform: "none" }}
        >
          {t(Resources.Common.Upload)}
        </Button>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, flex: 1 }}
        >
          {trainingFilesCount} {t(Resources.Actors.ActorDetail.Files)}
        </Typography>
        {trainingFilesCount > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteSweepIcon />}
            onClick={handleRemoveAllFiles}
            disabled={deleteAllMutation.isPending}
            sx={{ textTransform: "none" }}
          >
            {t(Resources.Actors.ActorDetail.RemoveAllFiles)}
          </Button>
        )}
      </Box>
    </Box>
  );

  // State A: Loading
  if (isLoadingDetail) {
    return (
      <BlModal
        isOpen={isOpen}
        title={t(Resources.Actors.ActorDetail.EditVoiceModal.Title)}
        onCancel={onClose}
        cancelButtonText={t(Resources.Common.Cancel)}
        maxWidth="800px"
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: Spacing.M }}>
          <CircularProgress />
        </Box>
      </BlModal>
    );
  }

  // State B: Upload files intermediate step
  if (needsFileUpload) {
    return (
      <BlModal
        isOpen={isOpen}
        title={t(Resources.Actors.ActorDetail.EditVoiceModal.Title)}
        onCancel={onClose}
        cancelButtonText={t(Resources.Common.Cancel)}
        maxWidth="800px"
      >
        <Stack spacing={Spacing.S}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: Spacing.XS,
              p: Spacing.S,
              backgroundColor: theme.colors.backgroundTile,
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: theme.palette.info.main, fontSize: 20 }}
            />
            <Typography variant="body2">
              {t(Resources.Actors.ActorDetail.UploadFilesRequired)}
            </Typography>
          </Box>

          {renderFileUploadArea()}
        </Stack>
      </BlModal>
    );
  }

  // State C: Edit form + preview
  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Actors.ActorDetail.EditVoiceModal.Title)}
      onCancel={onClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Actors.ActorDetail.EditVoiceModal.Save)}
      maxWidth="800px"
    >
      <Stack spacing={Spacing.M}>
        <Grid container spacing={Spacing.S}>
          <Grid size={6}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: Spacing.XS }}
            >
              {t(Resources.Actors.ActorDetail.TrainingModel)}
            </Typography>
            <Typography variant="body1">
              {t(Resources.Common.VoiceLlms[voiceDetail!.voiceLlm]) ??
                voiceDetail!.voiceLlm}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: Spacing.XS }}
            >
              {t(Resources.Actors.ActorDetail.ExternalVoiceID)}
            </Typography>
            <Typography variant="body1">
              {voiceDetail!.externalVoiceID || "-"}
            </Typography>
          </Grid>
        </Grid>

        {/* Basic info */}
        <Grid container spacing={Spacing.S}>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="name"
              label={t(Resources.Actors.ActorDetail.VoiceName)}
              errors={errors}
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="tags"
              label={t(Resources.Actors.ActorDetail.Tags)}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={Spacing.S}>
          <Grid size={6}>
            <BlFormDropdown
              control={control}
              name="language"
              label={t(Resources.Actors.ActorDetail.NativeLanguage)}
              options={getLanguageOptions()}
              errors={errors}
            />
          </Grid>
        </Grid>

        {/* Training files */}
        {renderFileUploadArea()}

        {/* Preview section */}
        {canShowPreview && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t(Resources.Actors.ActorDetail.DemoPreview)}
            </Typography>

            <Stack
              direction="column"
              spacing={2}
              sx={{
                padding: theme.spacing(2, 4),
                border: "solid 1px",
                borderColor: theme.colors.border,
                borderRadius: Spacing.S,
                overflowX: "hidden",
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ mb: Spacing.XS }}>
                  {t(Resources.Actors.ActorDetail.Speed)}:{" "}
                  {speedFactor.toFixed(1)}
                </Typography>
                <Slider
                  value={speedFactor}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  onChange={(_, value) => setSpeedFactor(value)}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: Spacing.XS }}>
                  {t(Resources.Actors.ActorDetail.Volume)}: {volumePercent} %
                </Typography>
                <Slider
                  value={volumePercent}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(_, value) => setVolumePercent(value)}
                />
              </Box>
            </Stack>

            <Button
              variant="contained"
              color="primary"
              onClick={handleGeneratePreview}
              disabled={generatePreviewMutation.isPending}
              fullWidth
              sx={{ textTransform: "none" }}
            >
              {generatePreviewMutation.isPending ? (
                <CircularProgress size={20} sx={{ mr: 1, color: "inherit" }} />
              ) : null}
              {t(Resources.Actors.ActorDetail.GeneratePreview)}
            </Button>

            {voiceDetail!.previews.length > 0 && (
              <Stack spacing={Spacing.S}>
                {voiceDetail!.previews.map((preview) => (
                  <ActorVoiceAudioPlayer
                    key={preview.fileID}
                    fileID={preview.fileID}
                    languageLabel={`${LANGUAGE_LABELS[preview.language] ?? preview.language} preview:`}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </BlModal>
  );
};

function buildDefaults(voice?: ActorVoiceDetailDto | null): EditVoiceFormData {
  if (!voice) {
    return {
      name: "",
      language: LanguageCodes.Czech,
      tags: "",
      externalEndpoint: "",
    };
  }

  return {
    name: voice.name,
    language: voice.language,
    tags: voice.tags || "",
    externalEndpoint: voice.externalVoiceID || "",
  };
}
